import React, { useEffect, useRef, useState } from "react";
import S from "./YoutubeCards.styled";
import { ColorOptions, IWPImage, IWPTitleParts } from "@/src/types";
import { Icon } from "@/styles/components";
import { gatsbyImageHelper, isLight } from "@/src/helpers";
import Youtube from "@/components/global-components/Youtube";
import { motion } from "framer-motion";
import TitleParts from "@/components/global-components/TitleParts";
import { motionAnimation } from "@/styles/global";

export interface IYoutubeCardsProps {
  blockId: string;
  backgroundColor: ColorOptions;
  titleParts: IWPTitleParts;
  title: string;
  youtubeCards: {
    url: string;
    title: string;
    subTitle: string;
    image: IWPImage;
  }[];
}

const YoutubeCards: React.FC<IYoutubeCardsProps> = ({
  blockId = "",
  backgroundColor,
  titleParts,
  title,
  youtubeCards,
}) => {
  const [scrolls, setScrolls] = useState(false);
  const [openModalIndex, setOpenModalIndex] = useState<number | null>(null);
  const contentWrapper = useRef<HTMLDivElement | null>(null);

  const cardColor = isLight(backgroundColor) ? "black" : "creme";
  const textColor = isLight(cardColor) ? "black" : "creme";

  useEffect(() => {
    if (typeof window === "undefined") return;

    const checkScrollability = () => {
      if (window.innerWidth > 1850 && contentWrapper.current) {
        setScrolls(
          contentWrapper.current.scrollWidth >
            contentWrapper.current.clientWidth
        );
      } else {
        setScrolls(false); // Sätt till false om bredden är mindre än 1850px
      }
    };

    checkScrollability();
    window.addEventListener("resize", checkScrollability);

    return () => {
      window.removeEventListener("resize", checkScrollability);
    };
  }, [youtubeCards]);

  useEffect(() => {
    if (scrolls && window.innerWidth >= 1024 && contentWrapper.current) {
      // Automatically scroll one card to the right on load when scrolls === true
      contentWrapper.current.scrollLeft += 250;
    }
  }, [scrolls]);

  const handlePress = (element: HTMLDivElement, step: number) => {
    let scrollAmount = 0;
    const slideTimer = setInterval(() => {
      // Scroll by the step value (either left or right)
      element.scrollLeft += step;
      scrollAmount += Math.abs(step);

      // Check if we've scrolled past the threshold
      if (scrollAmount >= 100) {
        clearInterval(slideTimer);
      }

      // Check if the scroll position is at the end
      if (element.scrollLeft + element.clientWidth >= element.scrollWidth) {
        // If we're at the end, scroll back to the start
        element.scrollLeft = 0;
        clearInterval(slideTimer);
      }
    }, 25);
  };

  const NavigationButton = ({ left }: { left?: boolean }) => (
    <div
      onClick={() =>
        handlePress(contentWrapper.current as HTMLDivElement, left ? -332 : 332)
      }
    >
      <Icon src={left ? "ArrowLeft" : "ArrowRight"} width={24} />
    </div>
  );

  const Modal = () => {
    if (openModalIndex === null) return null;
    const { url } = youtubeCards[openModalIndex];
    const wpVideo = {
      youtubeLink: url,
      image: youtubeCards[openModalIndex].image,
    };

    return (
      <>
        <S.Backdrop onClick={() => setOpenModalIndex(null)} />
        <motion.div
          initial={{ opacity: 0, visibility: "hidden" }}
          animate={{ opacity: 1, visibility: "visible" }}
          exit={{ opacity: 0, visibility: "hidden" }}
        >
          <S.Modal>
            <S.ModalInner>
              <Youtube aspectRatio={false} video={wpVideo} />
              <S.CloseButton onClick={() => setOpenModalIndex(null)}>
                <Icon src="Close" width={24} color="white" />
              </S.CloseButton>
            </S.ModalInner>
          </S.Modal>
        </motion.div>
      </>
    );
  };

  const renderCards = youtubeCards?.map(({ image, title, subTitle }, index) => {
    const key = `youtube-card-${index}`;
    const imageData = gatsbyImageHelper(image);
    return (
      <S.CardWrapper
        onClick={() => setOpenModalIndex(index)}
        key={key}
        $backgroundColor={cardColor}
      >
        <S.Card>
          {imageData && <S.Image alt={image.altText} image={imageData} />}
          <S.TextWrapper>
            <S.CardTextInner>
              <S.CardTitle $color={textColor}>{title && title}</S.CardTitle>
              {subTitle && (
                <S.CardSubTitle $color={textColor}>{subTitle}</S.CardSubTitle>
              )}
            </S.CardTextInner>
            <S.Button>
              <S.CardSmallHeading
                $color={isLight(cardColor) ? "black" : "creme"}
              >
                Watch Now
              </S.CardSmallHeading>
              <Icon src="ArrowRightOutline" width={14} color={"pink"} />
            </S.Button>
          </S.TextWrapper>
        </S.Card>
      </S.CardWrapper>
    );
  });

  return (
    <S.Section as={motion.section} {...motionAnimation} id={blockId}>
      {openModalIndex !== null && <Modal />}
      <S.Wrapper $backgroundColor={backgroundColor}>
        <S.Inner>
          <S.TitleArrowWrapper>
            {titleParts && (
              <S.Title>
                <TitleParts
                  titleParts={titleParts}
                  backgroundColor={backgroundColor}
                />
              </S.Title>
            )}
            {title && (
              <S.Title
                $color={
                  backgroundColor === "yellow"
                    ? "brown"
                    : isLight(backgroundColor)
                      ? "black"
                      : "creme"
                }
              >
                {title}
              </S.Title>
            )}
            {scrolls && (
              <S.ArrowsWrapper>
                <NavigationButton left />
                <NavigationButton />
              </S.ArrowsWrapper>
            )}
          </S.TitleArrowWrapper>
        </S.Inner>
        <S.ScrollContainer ref={contentWrapper}>
          <S.Container>{renderCards}</S.Container>
        </S.ScrollContainer>
      </S.Wrapper>
    </S.Section>
  );
};

export default YoutubeCards;
